import { inject, Injectable } from '@angular/core';
import { ToastsService } from '@catch/services';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { UsersAdapter } from '../../adapters/users.adapter';
import { confirmPauseWorkflows, workflowReferenceNames } from '../../functions/workflow-conflict';
import { StepRole } from '../../enums/step-role.enum';
import {
  createUser,
  createUserSuccess,
  deleteUser,
  deleteUserFailure,
  deleteUserSuccess,
  loadUsers,
  loadUsersFailure,
  loadUsersSuccess,
  resetUserTFA,
  resetUserTFAFailure,
  resetUserTFASuccess,
  updateUser,
  updateUserFailure,
  updateUserSuccess,
} from './users.actions';

@Injectable()
export class UsersEffects {
  private readonly actions$ = inject(Actions);
  private readonly usersAdapter = inject(UsersAdapter);
  private readonly toastsService = inject(ToastsService);
  private readonly transloco = inject(TranslocoService);
  
  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUsers),
      exhaustMap(() =>
        this.usersAdapter.getUsers().pipe(
          map((users) => {
            const mappedUsers = users.map((user: any) => ({
              ...user,
              roles: user.roles?.map((role: any) => ({
                stepId: role.stepId,
                role:
                  role.role === 1
                    ? StepRole.MANAGER
                    : role.role === 0
                    ? StepRole.VIEWER
                    : StepRole.NO_ROLE,
              })) || [],
            }));
            return loadUsersSuccess({ users: mappedUsers });
          }),
          catchError((error) => {
            return of(loadUsersFailure({ error }));
          })
        )
      )
    )
  );

  loadUsersFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUsersFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createUser),
      mergeMap((props) =>
        this.usersAdapter.createUser(props.user).pipe(
          map((response) =>
            createUserSuccess({ user: { ...props.user, id: response.id } })
          ),
          catchError((error) => {
            return of(deleteUserFailure({ error }));
          })
        )
      )
    )
  );

  createUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createUserSuccess),
        tap(() => {
          this.toastsService.success(
            'company-tool.views.settings.company.users.toasts.created'
          );
        })
      ),
    { dispatch: false }
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUser),
      mergeMap((props) =>
        this.usersAdapter.editUser(props.userId, props.role).pipe(
          map(() =>
            updateUserSuccess({ userId: props.userId, role: props.role })
          ),
          catchError((error) => {
            return of(updateUserFailure({ error }));
          })
        )
      )
    )
  );

  updateUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateUserSuccess),
        tap(() => {
          this.toastsService.success(
            'company-tool.views.settings.company.users.toasts.updated'
          );
        })
      ),
    { dispatch: false }
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteUser),
      mergeMap((props) =>
        this.usersAdapter.deleteUser(props.user, props.force).pipe(
          map(() => deleteUserSuccess({ id: props.user.id })),
          catchError((error) => {
            const names = workflowReferenceNames(error);
            if (names && !props.force && confirmPauseWorkflows(names, (name) => this.transloco.translate(name))) {
              return this.usersAdapter.deleteUser(props.user, true).pipe(
                map(() => deleteUserSuccess({ id: props.user.id })),
                catchError((retryError) => of(deleteUserFailure({ error: retryError }))),
              );
            }
            return of(deleteUserFailure({ error }));
          })
        )
      )
    )
  );

  deleteUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteUserSuccess),
        tap(() => {
          this.toastsService.success(
            'company-tool.views.settings.company.users.toasts.deleted'
          );
        })
      ),
    { dispatch: false }
  );

  resetUserTFA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetUserTFA),
      mergeMap((props) =>
        this.usersAdapter.resetTFA(props.user.id).pipe(
          map(() =>
            resetUserTFASuccess({ user: { ...props.user, hasTFA: false } })
          ),
          catchError((error) => {
            return of(resetUserTFAFailure({ error }));
          })
        )
      )
    )
  );

  resetUserTFASuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetUserTFASuccess),
        tap(() => {
          this.toastsService.success(
            'company-tool.views.settings.company.users.sections.users.tfa.reset-toast'
          );
        })
      ),
    { dispatch: false }
  );
}
